import { useState, useCallback } from "react";

export interface FunctionCall {
  id: string;
  name: string;
  args: Record<string, any>;
  state: "queued" | "running" | "completed";
  result?: string;
  startTime?: number;
  endTime?: number;
}

export interface TodoItem {
  id: string;
  content: string;
  status: "pending" | "in_progress" | "completed";
  priority: "high" | "medium" | "low";
}

export interface BookmarkItem {
  id: string;
  type:
    | "task"
    | "person"
    | "document"
    | "location"
    | "resource"
    | "idea"
    | "issue";
  title: string;
  reason: string;
}

export interface UserQuestion {
  id: string;
  question: string;
  timestamp: number;
  answered?: boolean;
  question_type?:
    | "string"
    | "number"
    | "date"
    | "datetime"
    | "range"
    | "single_select"
    | "multi_select";
  options?: string[];
  answer?: string;
}

export interface TaskAgentState {
  chatMessages: string;
  thinkingMessage: string;
  functionCalls: FunctionCall[];
  todos: TodoItem[];
  bookmarks: BookmarkItem[];
  userQuestions: UserQuestion[];
  isActive: boolean;
  lastEventTime: number;
}

export interface TaskAgentStateManager {
  state: TaskAgentState;
  actions: {
    handleWebSocketMessage: (channel: string, data: any) => void;
    resetState: () => void;
    updateState: (updates: Partial<TaskAgentState>) => void;
  };
  computed: {
    isCurrentlyActive: boolean;
    generalFunctionCalls: FunctionCall[];
  };
}

const initialState: TaskAgentState = {
  chatMessages: "",
  thinkingMessage: "",
  functionCalls: [],
  todos: [],
  bookmarks: [],
  userQuestions: [],
  isActive: false,
  lastEventTime: 0,
};

export const useTaskAgentState = (
  staticState?: Partial<TaskAgentState>,
): TaskAgentStateManager => {
  const [state, setState] = useState<TaskAgentState>({
    ...initialState,
    ...staticState,
  });

  const updateState = useCallback((updates: Partial<TaskAgentState>) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const resetState = useCallback(() => {
    setState(initialState);
  }, []);

  const handleWebSocketMessage = useCallback((channel: string, data: any) => {
    console.debug("Received message:", channel, data);
    const eventTime = Date.now();

    setState((prev) => ({ ...prev, lastEventTime: eventTime }));

    if (!data.eventType) {
      console.warn("No event type found in message");
      return;
    }

    switch (data.eventType) {
      case "user_message_started":
        setState((prev) => ({
          ...prev,
          chatMessages: "",
          thinkingMessage: "",
          functionCalls: [],
          isActive: true,
        }));
        break;

      case "chat_streaming":
        setState((prev) => ({
          ...prev,
          chatMessages: prev.chatMessages + data.content,
          isActive: true,
        }));
        break;

      case "thinking_streaming":
        setState((prev) => ({
          ...prev,
          thinkingMessage: data.content,
          isActive: true,
        }));
        break;

      case "function_call": {
        const functionCallData = JSON.parse(data.content);

        if (
          functionCallData.name === "TodoRead" ||
          functionCallData.name === "TodoWrite"
        ) {
          if (
            functionCallData.name === "TodoWrite" &&
            functionCallData.args?.todos
          ) {
            const todos = functionCallData.args.todos.map((todo: any) => ({
              id: todo.id,
              content: todo.content,
              status: todo.status,
              priority: todo.priority,
            }));
            setState((prev) => ({
              ...prev,
              todos: todos,
              isActive: true,
            }));
          }
          return;
        }

        if (
          functionCallData.name === "BookmarkRead" ||
          functionCallData.name === "BookmarkWrite"
        ) {
          if (
            functionCallData.name === "BookmarkWrite" &&
            functionCallData.args?.bookmarks
          ) {
            const bookmarks = functionCallData.args.bookmarks.map(
              (bookmark: any) => ({
                id: bookmark.id,
                type: bookmark.type,
                title: bookmark.title,
                reason: bookmark.reason,
              }),
            );
            setState((prev) => ({
              ...prev,
              bookmarks: [...prev.bookmarks, ...bookmarks],
              isActive: true,
            }));
          }
          return;
        }

        if (
          functionCallData.name === "user_input" ||
          functionCallData.name === "user_question"
        ) {
          const question: UserQuestion = {
            id: functionCallData.id,
            question: functionCallData.args?.question || "User input requested",
            timestamp: Date.now(),
            answered: false,
            question_type: functionCallData.args?.question_type,
            options: functionCallData.args?.options,
          };
          setState((prev) => ({
            ...prev,
            userQuestions: [...prev.userQuestions, question],
            isActive: true,
          }));
          return;
        }

        const functionCall: FunctionCall = {
          id: functionCallData.id,
          name: functionCallData.name,
          args: functionCallData.args || {},
          state: "queued",
          startTime: Date.now(),
        };
        setState((prev) => ({
          ...prev,
          functionCalls: [...prev.functionCalls, functionCall],
          isActive: true,
        }));
        break;
      }

      case "function_call_started": {
        const functionCallData = JSON.parse(data.content);

        if (
          functionCallData.name === "TodoRead" ||
          functionCallData.name === "TodoWrite" ||
          functionCallData.name === "BookmarkRead" ||
          functionCallData.name === "BookmarkWrite" ||
          functionCallData.name === "user_input" ||
          functionCallData.name === "user_question"
        ) {
          return;
        }

        const functionCallId = functionCallData.id;
        setState((prev) => ({
          ...prev,
          functionCalls: prev.functionCalls.map((fc) =>
            fc.id === functionCallId || fc.name === functionCallData.name
              ? { ...fc, state: "running", startTime: Date.now() }
              : fc,
          ),
          isActive: true,
        }));
        break;
      }

      case "function_call_completed": {
        const completedData = JSON.parse(data.content);
        const functionCallData = completedData.functionCall;

        if (
          functionCallData.name === "user_input" ||
          functionCallData.name === "user_question"
        ) {
          const questionId = functionCallData.id;
          // Extract answer from the tool result
          const answerMatch =
            completedData.toolResult?.match(/User answered: "([^"]*)"/) ||
            completedData.toolResult?.match(/User selected: (.+)$/);
          const answer = answerMatch ? answerMatch[1] : null;

          setState((prev) => ({
            ...prev,
            userQuestions: prev.userQuestions.map((q) =>
              q.id === questionId
                ? { ...q, answered: true, answer: answer || undefined }
                : q,
            ),
            isActive: true,
          }));
          return;
        }

        if (
          functionCallData.name === "TodoRead" ||
          functionCallData.name === "TodoWrite" ||
          functionCallData.name === "BookmarkRead" ||
          functionCallData.name === "BookmarkWrite"
        ) {
          return;
        }

        const functionCallId = functionCallData.id;
        setState((prev) => ({
          ...prev,
          functionCalls: prev.functionCalls.map((fc) =>
            fc.id === functionCallId || fc.name === functionCallData.name
              ? {
                  ...fc,
                  state: "completed",
                  result: completedData.toolResult,
                  endTime: Date.now(),
                }
              : fc,
          ),
          isActive: true,
        }));
        break;
      }
      case "chat_completed":
        setState((prev) => ({
          ...prev,
          isActive: false,
        }));
        break;

      default:
        console.warn("Unknown event type:", data.eventType);
    }
  }, []);

  const isCurrentlyActive =
    state.isActive &&
    state.lastEventTime > 0 &&
    Date.now() - state.lastEventTime < 5000;

  const generalFunctionCalls = state.functionCalls.filter(
    (fc) =>
      ![
        "TodoRead",
        "TodoWrite",
        "BookmarkRead",
        "BookmarkWrite",
        "user_input",
        "user_question",
      ].includes(fc.name),
  );

  return {
    state,
    actions: {
      handleWebSocketMessage,
      resetState,
      updateState,
    },
    computed: {
      isCurrentlyActive,
      generalFunctionCalls,
    },
  };
};
